import { Box, Unstable_Grid2 as Grid, Stack } from '@mui/material';

import { ContainerColumnLeft, Divider, InfoField, LinkChip } from '@occc-app/design-system';
import { formatThreshold } from '@occc-app/routes/Audience/utils';
import { routeCreator as loyaltyEventRouteCreator } from '@occc-app/routes/LoyaltyEvent/utils';
import { FULL_DATE_FORMAT, readableDateWithTimezone } from '@occc-app/lib/format';
import { eventTargetingConditionOperatorToLabelMap } from '@occc-app/routes/Audience/components/constants';
import { pluralize } from '@occc-app/lib/strings';

import { CardLayout } from '@occc/ui-design-system';
import { qualifiedEventsMemberPurchasedOperatorToTitleMap, titleByEventType } from './constants';

import { getTimePeriodText } from './getTimePeriodText';
import { mapQualifiedEventPurchaseConditionToOperator } from './mapQualifiedEventPurchaseConditionToOperator';
import { productCountingMap } from '../../constants';
import { QualifiedEventAttribute, QualifiedEventType } from '../../types';
import { ProductGroupsChipBox } from '../ProductGroupsChipBox';
import { LocationGroupsInfoField } from '../LocationGroupsInfoField';
import { LocationsInfoField } from '../LocationsInfoField';

type QualifiedEventsDetailsProps = {
  attributes: QualifiedEventAttribute[];
  createdAt: string;
};

export const QualifiedEventsDetails = ({ attributes, createdAt }: QualifiedEventsDetailsProps) => {
  return (
    <ContainerColumnLeft>
      <CardLayout title='Qualified Events'>
        <Stack divider={<Divider />} spacing={2}>
          <Box px={0.5}>
            <InfoField
              label='Tracking Events since'
              value={readableDateWithTimezone(createdAt, FULL_DATE_FORMAT)}
            />
          </Box>

          {attributes.map((attribute, i) => {
            const {
              purchaseCondition,
              times,
              loyaltyEvent,
              operator: timesOperator,
              audienceEventDateCondition,
            } = attribute;

            const countProductsBy = purchaseCondition?.countProductsBy;
            const threshold = purchaseCondition?.threshold;

            const type = mapQualifiedEventPurchaseConditionToOperator(purchaseCondition);
            const operatorTitle = qualifiedEventsMemberPurchasedOperatorToTitleMap[type];

            const eventType = purchaseCondition
              ? QualifiedEventType.MadePurchase
              : QualifiedEventType.FulfilledLoyaltyEvent;

            const timePeriodText = getTimePeriodText({
              relativeMonthsBack: audienceEventDateCondition?.relativeMonthBack?.relativeMonthsBack,
              timeWindow: audienceEventDateCondition?.timeWindow?.timeWindow,
              startDate: audienceEventDateCondition?.fixedDateRange?.startDate,
              endDate: audienceEventDateCondition?.fixedDateRange?.endDate,
            });

            return (
              <Grid container spacing={1} key={i}>
                <Grid xs={6} sm={3}>
                  <InfoField label='Member has' value={titleByEventType[eventType]} />
                </Grid>

                <Grid xs={6} sm={3}>
                  <InfoField
                    label='How many times'
                    value={`${
                      timesOperator
                        ? eventTargetingConditionOperatorToLabelMap[timesOperator]
                        : undefined
                    }${timesOperator && ' '}${times} ${pluralize(times, 'time')}`}
                  />
                </Grid>

                {timePeriodText && (
                  <Grid xs={6} sm={3}>
                    <InfoField label='Period of the Purchase' value={timePeriodText} />
                  </Grid>
                )}

                {eventType === QualifiedEventType.FulfilledLoyaltyEvent && (
                  <Grid>
                    <InfoField
                      label='Loyalty Event'
                      value={
                        loyaltyEvent ? (
                          <LinkChip
                            href={loyaltyEventRouteCreator.details(loyaltyEvent.id)}
                            label={loyaltyEvent.name}
                          />
                        ) : (
                          'None'
                        )
                      }
                    />
                  </Grid>
                )}

                {eventType === QualifiedEventType.MadePurchase && (
                  <>
                    <Grid xs={12}>
                      <InfoField
                        label='Product Purchased'
                        value={
                          <ProductGroupsChipBox
                            productGroups={purchaseCondition?.productGroups ?? []}
                          />
                        }
                        mb={0.5}
                      />
                    </Grid>

                    <Grid xs={6} sm={3}>
                      <InfoField
                        label='Member has Purchased'
                        value={`${operatorTitle} ${formatThreshold(
                          threshold ?? 0,
                          countProductsBy,
                        )}`}
                      />
                    </Grid>

                    <Grid xs={6} sm={3}>
                      <InfoField
                        label='Count products by'
                        value={countProductsBy ? productCountingMap[countProductsBy] : ''}
                      />
                    </Grid>

                    {!!purchaseCondition?.locations?.length && (
                      <Grid xs={12}>
                        <LocationsInfoField locations={purchaseCondition.locations} />
                      </Grid>
                    )}

                    {!!purchaseCondition?.locationGroups?.length && (
                      <Grid xs={12}>
                        <LocationGroupsInfoField
                          locationGroups={purchaseCondition.locationGroups}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            );
          })}
        </Stack>
      </CardLayout>
    </ContainerColumnLeft>
  );
};
